import type { InfiniteData, QueryClient, QueryKey } from "@tanstack/react-query";
import { ACTIVITY_PAGE_LIMIT, activityQueryKeys } from "@/hooks/activity/useActivity";
import { DEFAULT_ACTIVITY_STATS } from "@/hooks/activity/activityDefaults";
import type {
  AddOnAttention,
  ActivityDayLog,
  ActivityResponseDO,
  FetchActivityParams,
} from "@/types/activity";

export const applyAddOnAttentionToActivityCaches = (
  queryClient: QueryClient,
  checkInId: string,
  addOnAttention: AddOnAttention[],
) => {
  queryClient.setQueriesData<InfiniteData<ActivityResponseDO>>(
    { queryKey: activityQueryKeys.lists() },
    (current) => current && ({
      ...current,
      pages: current.pages.map((page) => ({
        ...page,
        dayLogs: page.dayLogs.map((entry) =>
          entry.id === checkInId ? { ...entry, addOnAttention } : entry),
      })),
    }),
  );
};

const normalizeSearchValue = (value?: string | null) =>
  value?.trim().toLowerCase() ?? "";

const getActivityListFilters = (
  queryKey: QueryKey,
): Pick<
  FetchActivityParams,
  "limit" | "search" | "status" | "startDate" | "endDate"
> | null => {
  const filters = queryKey[2];

  if (!filters || typeof filters !== "object") {
    return null;
  }

  return filters as Pick<
    FetchActivityParams,
    "limit" | "search" | "status" | "startDate" | "endDate"
  >;
};

const matchesActivityFilters = (
  activity: ActivityDayLog,
  filters: Pick<FetchActivityParams, "search" | "status">,
) => {
  const normalizedSearch = normalizeSearchValue(filters.search);
  const matchesSearch =
    normalizedSearch.length === 0 ||
    normalizeSearchValue(activity.member).includes(normalizedSearch);
  const matchesStatus = !filters.status || activity.status === filters.status;

  return matchesSearch && matchesStatus;
};

const getLocalDateString = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const shouldApplyLiveActivityToDateRange = (
  filters: Pick<FetchActivityParams, "startDate" | "endDate">,
) => {
  if (!filters.startDate && !filters.endDate) {
    return true;
  }

  const today = getLocalDateString(new Date());
  const startDate = filters.startDate || today;
  const endDate = filters.endDate || today;

  return startDate <= today && today <= endDate;
};

const getInclusiveDateCount = (
  filters: Pick<FetchActivityParams, "startDate" | "endDate">,
) => {
  const today = getLocalDateString(new Date());
  const startDate = filters.startDate || today;
  const endDate = filters.endDate || today;
  const startTime = new Date(`${startDate}T00:00:00`).getTime();
  const endTime = new Date(`${endDate}T00:00:00`).getTime();

  if (!Number.isFinite(startTime) || !Number.isFinite(endTime)) {
    return 1;
  }

  const dayCount = Math.floor((endTime - startTime) / 86_400_000) + 1;

  return Math.max(dayCount, 1);
};

export const applySocketActivityToActivityCaches = (
  queryClient: QueryClient,
  activity: ActivityDayLog,
) => {
  const queries = queryClient
    .getQueryCache()
    .findAll({ queryKey: activityQueryKeys.lists() });

  queries.forEach((query) => {
    const filters = getActivityListFilters(query.queryKey);

    if (
      !filters ||
      !shouldApplyLiveActivityToDateRange(filters) ||
      !matchesActivityFilters(activity, filters)
    ) {
      return;
    }

    queryClient.setQueryData<InfiniteData<ActivityResponseDO>>(
      query.queryKey,
      (cachedData) => {
        if (!cachedData?.pages.length) {
          return cachedData;
        }

        const alreadyCached = cachedData.pages.some((page) =>
          page.dayLogs.some((entry) => entry.id === activity.id),
        );

        if (alreadyCached) {
          return cachedData;
        }

        const [firstPage, ...remainingPages] = cachedData.pages;
        const firstPageStats = firstPage.stats ?? DEFAULT_ACTIVITY_STATS;
        const limit =
          firstPage.pagination.limit || filters.limit || ACTIVITY_PAGE_LIMIT;
        const checkInsInPeriod =
          (firstPageStats.checkInsInPeriod ??
            firstPageStats.checkInsToday ??
            0) + 1;
        const avgDailyCheckInsInPeriod =
          checkInsInPeriod / getInclusiveDateCount(filters);
        const remainingPagesWithoutDuplicate = remainingPages.map((page) => ({
          ...page,
          dayLogs: page.dayLogs.filter((entry) => entry.id !== activity.id),
        }));

        return {
          ...cachedData,
          pages: [
            {
              ...firstPage,
              stats: {
                ...firstPageStats,
                checkInsToday: (firstPageStats.checkInsToday ?? 0) + 1,
                checkInsInPeriod,
                avgDailyCheckInsInPeriod,
              },
              dayLogs: [activity, ...firstPage.dayLogs].slice(0, limit),
              pagination: {
                ...firstPage.pagination,
                total:
                  typeof firstPage.pagination.total === "number"
                    ? firstPage.pagination.total + 1
                    : firstPage.pagination.total,
              },
            },
            ...remainingPagesWithoutDuplicate,
          ],
        };
      },
    );
  });
};

// Checkout updates an existing attendance; it never adds a row or increments visit totals.
export const applyCheckoutToActivityCaches = (
  queryClient: QueryClient,
  event: Pick<import("@/types/attendance").CheckoutEvent, "checkInId" | "checkoutTimestamp" | "checkOutRecorder">,
) => {
  queryClient.setQueriesData<InfiniteData<ActivityResponseDO>>(
    { queryKey: activityQueryKeys.lists() },
    (current) => current && ({ ...current, pages: current.pages.map((page) => ({ ...page,
      dayLogs: page.dayLogs.map((entry) => entry.id === event.checkInId
        ? { ...entry, checkedOutAt: event.checkoutTimestamp, checkOutRecorder: event.checkOutRecorder, canCheckOut: false }
        : entry),
    })) }),
  );
};
